import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  Fragment
} from 'react'
import { Form } from 'formik'
import _keys from 'lodash/keys'
import _upperFirst from 'lodash/upperFirst'
import { EnableChoiceType } from '@purecarslabs/signal-components/ModalPrompt/types'
/* webpack-strip-block:removed */
import renderIf from '@purecarslabs/signal-utils/renderIf'
import { LeadFormSpecResponseType } from '@app-src/data/lead-form-spec/types'
import { LeadContactType } from '@app-src/data/leads/types'
import { debugLogForms } from '@app-src/debug'
import { MvhColorsType } from '@app-src/types'
import { FORM_FIELD_ACCEPT_TERMS } from './constants'
import {
  getModalLeadFormDisclaimerText,
  getModalLeadFormFieldDefinitions
} from './fields'
import {
  FormContents,
  FormError,
  FormOuter,
  FormSubmitError,
  FormTitle,
  StyledField,
  TermsConditionsBody,
  TermsConditionsHeader,
  TermsConditionsSpan
} from './styles'
import { FormSubmitErrorFieldType, SubmitFormikRefType } from './types'
import { CHOICE_ID_OK } from '@signal/components/ModalPrompt/constants'

type FormContentsPropsType = {
  accountName: string
  desiredFields: Array<string>
  formDataSpec: LeadFormSpecResponseType
  formName: string
  formSubmitErrorText?: string
  formSubmitFieldErrors?: Array<FormSubmitErrorFieldType>
  initialShowTermsAndConditions?: boolean
  isEmbedded?: boolean
  mvhColors?: MvhColorsType | null
  onNeedEnableChoice: () => EnableChoiceType | undefined
  setFormLoaded: React.Dispatch<React.SetStateAction<boolean>>
  submitFormikRef: SubmitFormikRefType
  testAlreadySubmitted?: boolean
  // the following properties come from formik
  errors: Record<string, string>
  handleChange: (evt: React.MouseEvent) => void
  handleSubmit: (evt: React.FormEvent) => void
  isSubmitting: boolean
  values: LeadContactType
}

// this component owns the form's contents within the Formik control
export const Contents: React.FC<FormContentsPropsType> = ({
  accountName,
  desiredFields,
  formDataSpec,
  formName,
  formSubmitErrorText,
  formSubmitFieldErrors,
  initialShowTermsAndConditions,
  isEmbedded = false,
  mvhColors = {},
  onNeedEnableChoice,
  setFormLoaded,
  submitFormikRef,
  testAlreadySubmitted = false,
  // the following properties come from formik
  errors,
  handleChange,
  handleSubmit,
  isSubmitting,
  values
}) => {
  const {
    backgroundColor,
    placeholderColor,
    textColor,
    titleColor
  } = mvhColors as MvhColorsType

  // state and hooks
  //////////////////

  const [everSubmitted, setEverSubmitted] = useState<boolean>(
    testAlreadySubmitted
  )
  const [hideTermsAndConditions, setHideTermsAndConditions] = useState(
    !initialShowTermsAndConditions
  )
  const [
    remainingFormSubmitErrorFields,
    setRemainingFormSubmitErrorFields
  ] = useState<FormSubmitErrorFieldType[]>([])

  if (submitFormikRef) {
    submitFormikRef.current = handleSubmit
  }

  // memos
  ////////

  const formTestId = isEmbedded ? 'embed-form' : 'modal-form'

  const aggregatedErrors = useMemo(
    () =>
      remainingFormSubmitErrorFields.reduce(
        (_errors, { fieldName, message }) => {
          _errors[fieldName] = message
          return _errors
        },
        errors
      ),
    [errors, remainingFormSubmitErrorFields]
  )

  const firstErrorKey = useMemo(() => {
    const errorKeys = _keys(aggregatedErrors)
    return errorKeys.length && errorKeys[0]
  }, [aggregatedErrors])

  const formFieldDefinitions = useMemo(
    () => getModalLeadFormFieldDefinitions(desiredFields, formDataSpec),
    [desiredFields, formDataSpec]
  )

  // callbacks
  ////////////

  const shouldDisplayError = useCallback(
    (name: string): boolean =>
      (everSubmitted && firstErrorKey === name) ||
      Boolean(
        remainingFormSubmitErrorFields.find(
          ({ fieldName }) => fieldName === name
        )
      ),
    [everSubmitted, firstErrorKey, remainingFormSubmitErrorFields]
  )

  const handleFieldChange = useCallback(
    (name: string) => (evt: React.MouseEvent) => {
      // built in formik handleChange
      handleChange(evt)

      // remove any remaining form field error related to this field
      setRemainingFormSubmitErrorFields(remainingFormSubmitErrorFields =>
        remainingFormSubmitErrorFields.filter(
          ({ fieldName }) => fieldName !== name
        )
      )
    },
    [handleChange, setRemainingFormSubmitErrorFields]
  )

  const handleToggleTermsAndConditions = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation()
      evt.preventDefault()
      setHideTermsAndConditions(
        hideTermsAndConditions => !hideTermsAndConditions
      )
    },
    [setHideTermsAndConditions]
  )

  // effects
  //////////

  useEffect(() => {
    if (setFormLoaded) {
      setFormLoaded(true)
    }
    return () => {
      /* webpack-strip-block:removed */
        setFormLoaded(false)
        /* webpack-strip-block:removed */
    }
  }, [setFormLoaded])

  useEffect(() => {
    if (isSubmitting) {
      setEverSubmitted(true)
    }
  }, [isSubmitting, setEverSubmitted])

  useEffect(() => {
    const enableChoice = onNeedEnableChoice && onNeedEnableChoice()
    if (enableChoice && everSubmitted) {
      const shouldEnable = !isSubmitting && !Boolean(firstErrorKey)
      debugLogForms('enabling submit button', shouldEnable)
      enableChoice(CHOICE_ID_OK, shouldEnable)
    }
  }, [isSubmitting, everSubmitted, firstErrorKey, onNeedEnableChoice])

  useEffect(() => {
    if (formSubmitFieldErrors) {
      setRemainingFormSubmitErrorFields(formSubmitFieldErrors)
    }
  }, [formSubmitFieldErrors, setRemainingFormSubmitErrorFields])

  return (
    <FormOuter
      /* webpack-strip-block:removed */
      $isEmbedded={isEmbedded}
    >
      {renderIf(!isEmbedded, () => (
        <FormTitle $titleColor={titleColor}>{formName}</FormTitle>
      ))}
      <FormContents
        $backgroundColor={backgroundColor}
        $everSubmitted={everSubmitted}
        $isEmbedded={isEmbedded}
        $textColor={textColor}
      >
        {/* @ts-ignore */}
        <Form onSubmit={handleSubmit}>
          {formFieldDefinitions.map(
            ({
              name,
              placeholder,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              validateFunc,
              ...fieldProps
            }) => (
              <Fragment key={name}>
                <StyledField
                  name={name}
                  $backgroundColor={backgroundColor}
                  $textColor={textColor}
                  $hasError={shouldDisplayError(name)}
                  $alsoRequired={Boolean(aggregatedErrors[name])}
                  $anyHaveError={
                    everSubmitted && !remainingFormSubmitErrorFields.length
                  }
                  $placeholderColor={placeholderColor}
                  onChange={handleFieldChange(name)}
                  placeholder={
                    Boolean(aggregatedErrors[name])
                      ? `*${placeholder}`
                      : placeholder
                  }
                  {...fieldProps}
                />
                {renderIf(shouldDisplayError(name), () => (
                  <FormError
                    className="form-error"
                    /* webpack-strip-block:removed */
                  >
                    {_upperFirst(aggregatedErrors[name])}
                  </FormError>
                ))}
              </Fragment>
            )
          )}
          <TermsConditionsHeader className="terms-toggle">
            {renderIf(
              Boolean(values.phone),
              () => (
                <>
                  <input
                    className={FORM_FIELD_ACCEPT_TERMS}
                    name={FORM_FIELD_ACCEPT_TERMS}
                    type="checkbox"
                    onClick={handleFieldChange(FORM_FIELD_ACCEPT_TERMS)}
                    /* webpack-strip-block:removed */
                  />
                  <label
                    htmlFor={FORM_FIELD_ACCEPT_TERMS}
                    onClick={handleToggleTermsAndConditions}
                    /* webpack-strip-block:removed */
                  >
                    By entering your number, you agree to receive mobile
                    messages. Message frequency varies. Message and data rates
                    may apply.{' '}
                    <a href="#terms-conditions">
                      View our Privacy and Messaging Policy.
                    </a>
                  </label>
                </>
              ),
              () => (
                <TermsConditionsSpan
                  /* webpack-strip-block:removed */
                  onClick={handleToggleTermsAndConditions}
                >
                  By clicking submit you agree to these{' '}
                  <a href="#terms-conditions">terms and conditions.</a>
                </TermsConditionsSpan>
              )
            )}
          </TermsConditionsHeader>
          {renderIf(!hideTermsAndConditions, () => (
            <TermsConditionsBody
              $mustAcceptTerms={Boolean(values.phone)}
              /* webpack-strip-block:removed */
            >
              {getModalLeadFormDisclaimerText(accountName, formDataSpec)}
            </TermsConditionsBody>
          ))}
          {renderIf(shouldDisplayError(FORM_FIELD_ACCEPT_TERMS), () => (
            <FormError
              className="form-error accept-terms-error"
              /* webpack-strip-block:removed */
            >
              {_upperFirst(aggregatedErrors[FORM_FIELD_ACCEPT_TERMS])}
            </FormError>
          ))}
          {renderIf(Boolean(formSubmitErrorText), () => (
            <FormSubmitError className="form-error">
              {_upperFirst(formSubmitErrorText)}. Please try again.
            </FormSubmitError>
          ))}
        </Form>
      </FormContents>
    </FormOuter>
  )
}

export default memo(Contents)
